import { useMemo, useState } from "react";

import type { Dayjs } from "dayjs";
import { noop } from "lodash";

import { shortMonthDayAndYear } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import type { IComparedDuration } from "@similarweb/ui-components";
import {
    Button,
    DurationSelectorStateless,
    EGranularity,
    IconButton,
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import type { DatesRange } from "app/pages/sales-intelligence/sub-modules/account-overview/shared/types/common";

interface ISidePanelDatePickerProps {
    date: Dayjs;
    onDateChange: (date: Dayjs) => void;
    supportedDates: Dayjs[];
    isDisabled?: boolean;
}

export const SidePanelDatePicker = ({ date, onDateChange, supportedDates, isDisabled }: ISidePanelDatePickerProps) => {
    const [sameDateClickedByUser, setSameDateClickedByUser] = useState(false);
    const sortedAvailableDates = useMemo<Dayjs[]>(
        () => [...(supportedDates ?? [])].sort((a, b) => a.valueOf() - b.valueOf()) ?? [],
        [supportedDates],
    );

    const durationRange: DatesRange = {
        from: sortedAvailableDates?.[0],
        to: sortedAvailableDates?.[sortedAvailableDates.length - 1],
    };

    const currentIndex = useMemo<number>(() => {
        return sortedAvailableDates.findIndex((availableDate) =>
            availableDate.dayOfYear() === date.dayOfYear(),
        );
    }, [sortedAvailableDates, date]);

    const durationObj: IComparedDuration = {
        primary: { from: date.utc(true), to: date.utc(true) },
        secondary: null,
    };

    const prevDate = useMemo(() => {
        if (currentIndex > 0) return sortedAvailableDates[currentIndex - 1];
        if (currentIndex === -1 && sortedAvailableDates.length > 0) {
            return sortedAvailableDates.findLast((d) => d.valueOf() < date.valueOf());
        }
        return undefined;
    }, [currentIndex, sortedAvailableDates, date]);

    const nextDate = useMemo(() => {
        if (currentIndex !== -1 && currentIndex < sortedAvailableDates.length - 1) {
            return sortedAvailableDates[currentIndex + 1];
        }
        if (currentIndex === -1 && sortedAvailableDates.length > 0) {
            return sortedAvailableDates.find((d) => d.valueOf() > date.valueOf());
        }
        return undefined;
    }, [currentIndex, sortedAvailableDates, date]);

    const onPrevDate = (): void => {
        if (prevDate) {
            onDateChange(prevDate);
        }
    };

    const onNextDate = (): void => {
        if (nextDate) {
            onDateChange(nextDate);
        }
    };

    return (
        <Popover placement="bottom-start">
            <Flex alignItems="center">
                <IconButton
                    type="flat"
                    iconName="chevron-left"
                    iconSize="12px"
                    isDisabled={isDisabled || !prevDate}
                    onClick={onPrevDate}
                />
                <PopoverTrigger style={{ background: "none", padding: 0, pointerEvents: isDisabled ? "none" : "auto" }}>
                    <Button type="flat" isDisabled={isDisabled}>
                        <Text
                            fontVariant="captionTitle"
                            color={isDisabled ? colorsPalettes.carbon[200] : colorsPalettes.carbon[400]}
                        >
                            {durationObj.primary.from.format(shortMonthDayAndYear)}
                        </Text>
                    </Button>
                </PopoverTrigger>
                <IconButton
                    type="flat"
                    iconName="chevron-right"
                    iconSize="12px"
                    isDisabled={isDisabled || !nextDate}
                    onClick={onNextDate}
                />
            </Flex>
            <PopoverContent className="DropdownButton-popup-container">
                <DurationSelectorStateless
                    presets={[]}
                    presetsLabel=""
                    customPresetText=""
                    minDate={durationRange.from}
                    maxDate={durationRange.to}
                    minDailyDate={durationRange.from}
                    maxDailyDate={durationRange.to}
                    selectedPreset={undefined}
                    compareAllowed={false}
                    compareLabel=""
                    useDailyCalendar
                    isSingleDaySelection
                    compareDisabledTooltipText=""
                    compareSelected={false}
                    compareEnabled={false}
                    compareTypesList={[]}
                    compareTypesSelected=""
                    compareDurationSelected={durationObj}
                    durationChange={(duration) => onDateChange(duration.from)}
                    selectedGranularity={EGranularity.DAILY}
                    onSameDateClickedByUser={setSameDateClickedByUser}
                    sameDateClickedByUser={sameDateClickedByUser}
                    presetSelect={noop}
                    compareToggle={noop}
                    compareSelect={noop}
                    disableFooter
                    showSingleCalendar
                    supportedDates={supportedDates}
                />
            </PopoverContent>
        </Popover>
    );
};
